import { toUpperCase } from './helpers/string';

const manageUserPrivacySettingsKeys = [
	'name',
	'nickname',
	'zoneinfo',
	'picture',
	'email',
	'phoneNumber',
	'jobTitle',
	'organization',
	'department',
	'location',
] as const;

export type ManageUserPrivacySettingsKeys = (typeof manageUserPrivacySettingsKeys)[number];

const schemaKeys = {
	name: 'name',
	nickname: 'nickname',
	zoneinfo: 'zoneinfo',
	picture: 'picture',
	email: 'email',
	phoneNumber: 'phoneNumber',
	jobTitle: 'jobTitle',
	organization: 'organization',
	department: 'department',
	location: 'location',
} as const;

const apiKeys = {
	name: 'name',
	nickname: 'nickname',
	zoneinfo: 'zoneinfo',
	picture: 'picture',
	email: 'email',
	phoneNumber: 'extended_profile.phone_number',
	jobTitle: 'extended_profile.job_title',
	organization: 'extended_profile.organization',
	department: 'extended_profile.department',
	location: 'extended_profile.location',
} as const;

export type ManageUserPrivacySettingsSchemaValue = (typeof schemaKeys)[keyof typeof schemaKeys];
export type ManageUserPrivacySettingsApiValue = (typeof apiKeys)[keyof typeof apiKeys];

export const PRIVACY_SETTINGS_SCHEMA_TO_API_MAP: Record<
	ManageUserPrivacySettingsSchemaValue,
	ManageUserPrivacySettingsApiValue
> = {
	[schemaKeys.name]: apiKeys.name,
	[schemaKeys.nickname]: apiKeys.nickname,
	[schemaKeys.zoneinfo]: apiKeys.zoneinfo,
	[schemaKeys.picture]: apiKeys.picture,
	[schemaKeys.email]: apiKeys.email,
	[schemaKeys.phoneNumber]: apiKeys.phoneNumber,
	// extended profile
	[schemaKeys.jobTitle]: apiKeys.jobTitle,
	[schemaKeys.organization]: apiKeys.organization,
	[schemaKeys.department]: apiKeys.department,
	[schemaKeys.location]: apiKeys.location,
};

/**
 * Convert shape used to communicate with privacy APIs to shape that is used within our app.
 */
export const transformPrivacySettingsFormatFromApiToSchema = (
	data: Record<ManageUserPrivacySettingsApiValue, string>,
) =>
	Object.keys(PRIVACY_SETTINGS_SCHEMA_TO_API_MAP).reduce<Record<string, string | null>>(
		(memo, schemaValue) => {
			const apiResponseKey =
				PRIVACY_SETTINGS_SCHEMA_TO_API_MAP[schemaValue as ManageUserPrivacySettingsSchemaValue];
			const privacySetting = data[apiResponseKey];

			memo[schemaValue as ManageUserPrivacySettingsSchemaValue] = toUpperCase(privacySetting);
			return memo;
		},
		{},
	);

export const PRIVACY_SETTINGS_API_TO_SCHEMA_MAP: Record<
	ManageUserPrivacySettingsApiValue,
	ManageUserPrivacySettingsSchemaValue
> = {
	[apiKeys.name]: schemaKeys.name,
	[apiKeys.nickname]: schemaKeys.nickname,
	[apiKeys.zoneinfo]: schemaKeys.zoneinfo,
	[apiKeys.picture]: schemaKeys.picture,
	[apiKeys.email]: schemaKeys.email,
	[apiKeys.phoneNumber]: schemaKeys.phoneNumber,
	// extended profile
	[apiKeys.jobTitle]: schemaKeys.jobTitle,
	[apiKeys.organization]: schemaKeys.organization,
	[apiKeys.department]: schemaKeys.department,
	[apiKeys.location]: schemaKeys.location,
};

/**
 * Convert shape that is used within our app to shape used to communicate with privacy APIs.
 */

export const transformPrivacySettingsFormatFromSchemaToApi = (
	data: Record<ManageUserPrivacySettingsSchemaValue, string>,
) =>
	Object.keys(PRIVACY_SETTINGS_API_TO_SCHEMA_MAP).reduce<Record<string, string>>(
		(memo, apiValue) => {
			const apiResponseKey =
				PRIVACY_SETTINGS_API_TO_SCHEMA_MAP[apiValue as ManageUserPrivacySettingsApiValue];
			const privacySetting = data[apiResponseKey];
			if (privacySetting) {
				memo[apiValue] = (privacySetting as string).toLowerCase();
			}
			return memo;
		},
		{},
	);
